import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Bell, ShoppingBag, Star, X, CheckCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

export default function AdminNotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [unread, setUnread] = useState(0);
  const [seen, setSeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("seikat_seen_orders") || "[]")) }
    catch { return new Set(); }
  });
  const sinceRef = useRef(null);
  const intervalRef = useRef(null);

  const poll = useCallback(async () => {
    if (!user?.role?.includes("admin")) return;
    try {
      const params = sinceRef.current ? `?since=${sinceRef.current}` : "";
      const res = await axios.get(`${API}/admin/notifications${params}`, { withCredentials: true });
      sinceRef.current = res.data.timestamp;
      setData(res.data);
      const newOrders = (res.data.new_orders || []).filter(o => !seen.has(o.order_id));
      setUnread((res.data.pending_payment_count || 0) + (res.data.pending_reviews_count || 0));
    } catch { }
  }, [user, seen]);

  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, 20000); // every 20s
    return () => clearInterval(intervalRef.current);
  }, [poll]);

  const markAllRead = () => {
    const ids = (data?.new_orders || []).map(o => o.order_id);
    const newSeen = new Set([...seen, ...ids]);
    setSeen(newSeen);
    localStorage.setItem("seikat_seen_orders", JSON.stringify([...newSeen]));
    setUnread(0);
    setOpen(false);
  };

  if (!user?.role?.includes("admin")) return null;

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-9 h-9 rounded-xl hover:bg-seikat-pink/10 flex items-center justify-center text-[#4A453F] hover:text-seikat-pink transition-colors"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] px-1 bg-seikat-pink text-white text-[9px] rounded-full flex items-center justify-center font-body" data-testid="unread-badge">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-[#D4859B]/15 overflow-hidden">
            <div className="px-4 py-3.5 border-b border-[#D4859B]/10 flex items-center justify-between">
              <h3 className="font-body text-sm font-medium text-[#1C1A19]">Notifications</h3>
              {data?.new_orders?.length > 0 && (
                <button onClick={markAllRead} className="text-[10px] text-seikat-pink hover:text-seikat-bone transition-colors flex items-center gap-1">
                  <CheckCheck size={11} /> Mark read
                </button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {/* Pending counts */}
              <div className="grid grid-cols-2 gap-0 border-b border-[#D4859B]/10">
                <Link to="/admin/orders-board" onClick={() => setOpen(false)} className="flex flex-col items-center py-4 hover:bg-[#FDF8F6] transition-colors border-r border-[#D4859B]/10">
                  <ShoppingBag size={18} className="text-seikat-pink mb-1" />
                  <span className="font-heading text-2xl text-[#1C1A19]">{data?.pending_payment_count || 0}</span>
                  <span className="font-body text-[9px] tracking-widest uppercase text-[#A8A29A]">Pending Pay.</span>
                </Link>
                <Link to="/admin/reviews" onClick={() => setOpen(false)} className="flex flex-col items-center py-4 hover:bg-[#FDF8F6] transition-colors">
                  <Star size={18} className="text-[#C5DB7B] mb-1" />
                  <span className="font-heading text-2xl text-[#1C1A19]">{data?.pending_reviews_count || 0}</span>
                  <span className="font-body text-[9px] tracking-widest uppercase text-[#A8A29A]">Reviews</span>
                </Link>
              </div>

              {/* Recent orders */}
              {data?.new_orders?.length > 0 ? (
                <div className="divide-y divide-[#D4859B]/8">
                  {data.new_orders.slice(0, 8).map(order => (
                    <Link
                      key={order.order_id}
                      to={`/admin/orders-board`}
                      onClick={() => setOpen(false)}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-[#FDF8F6] transition-colors ${
                        seen.has(order.order_id) ? "opacity-60" : ""
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-seikat-pink/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ShoppingBag size={13} className="text-seikat-pink" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-xs font-medium text-[#1C1A19] truncate">{order.delivery_name || "New Order"}</p>
                        <p className="font-body text-xs text-[#8A857D]">{fmt(order.total)} · {order.status?.replace(/_/g, " ")}</p>
                        <p className="font-body text-[10px] text-[#A8A29A]">{order.created_at?.slice(0, 16).replace("T", " ")}</p>
                      </div>
                      {!seen.has(order.order_id) && (
                        <div className="w-2 h-2 rounded-full bg-seikat-pink flex-shrink-0 mt-2" />
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-center">
                  <Bell size={24} className="mx-auto text-[#D4859B]/30 mb-2" />
                  <p className="font-body text-[#A8A29A] text-sm">No recent orders</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
